import * as React from "react";
import { Dropdown } from "@mui/base/Dropdown";
import { Menu } from "@mui/base/Menu";
import { MenuButton as BaseMenuButton } from "@mui/base/MenuButton";
import { MenuItem as BaseMenuItem, menuItemClasses } from "@mui/base/MenuItem";
import { styled } from "@mui/system";
import { ORDER_STATUSES, ORDER_STATUS_CHOICES } from "constants/orderStatus";
import { readApiError } from "utils/apiError";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMotorcycle } from "@fortawesome/free-solid-svg-icons";

export default function MenuSimple(props) {
    const updateStatus = async (status) => {
        const fm = new FormData();
        fm.append("status", status);
        const result = await props.updateOrder(props.id, fm);
        // The server's own sentence, not a generic one: a refused status change
        // says WHY (a settled wallet order is locked, the driver has no
        // balance), and the operator can act on that.
        const failure = readApiError(result, "فشل تحديث حالة الطلب");
        if (failure) {
            props.showMessage?.("error", "هناك خطأ", failure);
        }
    };

    const label = ORDER_STATUSES.find((e) => e.en === props.status)?.ar;
    // Two facts, see utils/orderStatusStage.js.
    // `withDriver`: the order is still at the restaurant and a driver has it.
    //   The button has another colour.
    // `driverMark`: a driver has the order, before or after the pickup. The
    //   button has the motorcycle mark.
    let title;
    if (props.withDriver) title = `${label} — مع سائق، لم يستلم بعد`;
    else if (props.driverMark) title = `${label} — مع سائق`;

    return (
        <Dropdown>
            <MenuButton
                className={`${props.status}${props.withDriver ? " status--with-driver" : ""}`}
                title={title}
            >
                {label}
                {props.driverMark ? (
                    <FontAwesomeIcon icon={faMotorcycle} className="status__driver-mark" />
                ) : null}
            </MenuButton>
            <Menu slots={{ listbox: Listbox }}>
                {ORDER_STATUS_CHOICES.filter((s) => s.en !== props.status).map((e) => (
                    <MenuItem
                        onClick={() => {
                            updateStatus(e.en);
                        }}
                        key={e.en}
                    >
                        {e.ar}
                    </MenuItem>
                ))}
            </Menu>
        </Dropdown>
    );
}

const grey = {
    50: "#F3F6F9",
    100: "#E5EAF2",
    200: "#DAE2ED",
    300: "#C7D0DD",
    400: "#B0B8C4",
    500: "#9DA8B7",
    600: "#6B7A90",
    700: "#434D5B",
    800: "#303740",
    900: "#1C2025",
};

const Listbox = styled("ul")(
    ({ theme }) => `
  font-family: 'cairo', sans-serif;
  font-size: 0.875rem;
  box-sizing: border-box;
  padding: 6px;
  margin: 12px 0;
  min-width: 120px;
  border-radius: 12px;
  overflow: auto;
  outline: 0;
  background: ${theme.palette.mode === "dark" ? grey[900] : "#fff"};
  border: 1px solid ${theme.palette.mode === "dark" ? grey[700] : grey[200]};
  color: ${theme.palette.mode === "dark" ? grey[300] : grey[900]};
  box-shadow: 0 4px 6px ${
      theme.palette.mode === "dark" ? "rgba(0,0,0, 0.50)" : "rgba(0,0,0, 0.05)"
  };
  z-index: 1;
  `,
);

const MenuItem = styled(BaseMenuItem)(
    ({ theme }) => `
  list-style: none;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;

  &:last-of-type {
    border-bottom: none;
  }

  &:focus {
    background-color: ${theme.palette.mode === "dark" ? grey[800] : grey[100]};
    color: ${theme.palette.mode === "dark" ? grey[300] : grey[900]};
  }

  &.${menuItemClasses.disabled} {
    color: ${theme.palette.mode === "dark" ? grey[700] : grey[400]};
  }
  `,
);

const MenuButton = styled(BaseMenuButton)(
    ({ theme }) => `
  font-family: 'cairo', sans-serif;
  font-weight: 600;
  /* At least 100px, and wider for a label with the driver mark: at a fixed
     100px the mark was pushed out of the button. */
  min-width:100px;
  white-space:nowrap;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:6px;
  font-size: 0.875rem;
  line-height: 1.5;
  padding: 8px 16px;
  border-radius: 8px;
  transition: all 150ms ease;
  cursor: pointer;
  color:#fff;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);

  &:hover {
    opacity:0.7
  }

  &:active {
    background: ${theme.palette.mode === "dark" ? grey[700] : grey[100]};
  }
  `,
);
